// Phase SIKA-4b — Verified Return Certificate hook.
//
// Auth-only. Reads the caller's own sika_certificates row by goal_id.
// Returns the manifest + hash + generated_at. RLS enforces own-row read.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface SikaCertificate {
  certificate_id: string;
  goal_id: string;
  user_id: string;
  manifest: Record<string, unknown>;
  manifest_hash: string;
  generated_at: string;
}

export interface UseSikaCertificateState {
  certificate: SikaCertificate | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSikaCertificate(goalId: string | null): UseSikaCertificateState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [certificate, setCertificate] = useState<SikaCertificate | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(goalId));
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!user || !goalId) {
      setCertificate(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: qErr } = await (supabase
      .from("sika_certificates" as never) as unknown as {
        select: (cols: string) => {
          eq: (a: string, b: string) => {
            maybeSingle: () => Promise<{
              data: SikaCertificate | null;
              error: { message: string } | null;
            }>;
          };
        };
      })
      .select("certificate_id,goal_id,user_id,manifest,manifest_hash,generated_at")
      .eq("goal_id", goalId)
      .maybeSingle();
    if (qErr) {
      setCertificate(null);
      setError(qErr.message);
      setLoading(false);
      return;
    }
    setCertificate((data as SikaCertificate | null) ?? null);
    setLoading(false);
  }, [supabase, user, goalId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { certificate, loading, error, refetch };
}
